import { useState } from "react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { LockKey, Eye, EyeSlash, CircleNotch, CheckCircle, WarningCircle } from "@phosphor-icons/react";

const MIN_LENGTH = 8;
const EMPTY = { current: "", next: "", confirm: "" };

function PasswordField({ id, label, value, onChange, placeholder, autoComplete, invalid }) {
  const [show, setShow] = useState(false);
  return (
    <label className="block text-xs font-semibold" style={{ color: "var(--bk-text-2)" }} htmlFor={id}>
      {label}
      <span className="relative block mt-1.5">
        <input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          autoComplete={autoComplete}
          aria-invalid={invalid || undefined}
          data-testid={id}
          className="w-full rounded-md border px-3 py-2.5 pr-11 text-sm outline-none bg-white"
          style={invalid ? { borderColor: "var(--bk-energy)", boxShadow: "0 0 0 3px rgba(214, 90, 49, 0.18)", color: "var(--bk-text)" } : { borderColor: "var(--bk-border)", color: "var(--bk-text)" }}
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          aria-label={show ? `Hide ${label.toLowerCase()}` : `Show ${label.toLowerCase()}`}
          aria-pressed={show}
          data-testid={`${id}-toggle`}
          className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 rounded-full hover:bg-[#F8F7F4]"
          style={{ color: "var(--bk-text-2)" }}
        >
          {show ? <EyeSlash size={18} /> : <Eye size={18} />}
        </button>
      </span>
    </label>
  );
}

export function ChangePasswordCard() {
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState("");
  const [errorField, setErrorField] = useState("");
  const [success, setSuccess] = useState("");
  const [saving, setSaving] = useState(false);

  const set = (key) => (val) => { setForm((f) => ({ ...f, [key]: val })); setError(""); setErrorField(""); setSuccess(""); };
  const filled = form.current && form.next && form.confirm;
  const dirty = form.current || form.next || form.confirm;

  const fail = (field, msg) => { setErrorField(field); setError(msg); };

  const submit = async (e) => {
    e.preventDefault();
    setSuccess("");
    if (!form.current) return fail("current", "Enter your current password.");
    if (form.next.length < MIN_LENGTH) return fail("next", `New password must be at least ${MIN_LENGTH} characters.`);
    if (form.next !== form.confirm) return fail("confirm", "New password and confirmation don't match.");
    if (form.next === form.current) return fail("next", "New password must be different from your current password.");
    setSaving(true);
    try {
      const { data } = await api.post("/auth/change-password", { current_password: form.current, new_password: form.next, confirm_password: form.confirm });
      setForm(EMPTY);
      setSuccess(data?.message || "Password updated.");
      toast.success("Password updated");
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Couldn't update your password. Nothing was changed.";
      fail(/current password/i.test(msg) ? "current" : /match/i.test(msg) ? "confirm" : "next", msg);
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => { setForm(EMPTY); setError(""); setErrorField(""); setSuccess(""); };

  return (
    <section className="bk-card p-6 mt-6" data-testid="settings-password-section">
      <div className="flex items-start gap-3">
        <span className="w-10 h-10 rounded-xl flex items-center justify-center shrink-0" style={{ background: "var(--bk-rowan-bg)", color: "var(--bk-accent)" }}>
          <LockKey size={20} weight="duotone" />
        </span>
        <div>
          <p className="text-xs uppercase tracking-[0.18em] font-semibold" style={{ color: "var(--bk-accent)" }}>Security</p>
          <h2 className="font-display text-2xl font-bold mt-1" style={{ color: "var(--bk-text)" }}>Change password</h2>
          <p className="text-sm mt-1 max-w-xl" style={{ color: "var(--bk-text-2)" }}>We check your current password first. New passwords need at least {MIN_LENGTH} characters — nothing changes until everything checks out.</p>
        </div>
      </div>

      <form onSubmit={submit} className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl" data-testid="change-password-form" noValidate>
        <PasswordField id="password-current" label="Current password" value={form.current} onChange={set("current")} placeholder="Your current password" autoComplete="current-password" invalid={errorField === "current"} />
        <PasswordField id="password-new" label="New password" value={form.next} onChange={set("next")} placeholder={`At least ${MIN_LENGTH} characters`} autoComplete="new-password" invalid={errorField === "next"} />
        <PasswordField id="password-confirm" label="Confirm new password" value={form.confirm} onChange={set("confirm")} placeholder="Type it again" autoComplete="new-password" invalid={errorField === "confirm"} />

        <div className="md:col-span-3 min-h-[1.25rem]">
          {error && (
            <p role="alert" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-energy)" }} data-testid="password-error">
              <WarningCircle size={16} weight="fill" /> {error}
            </p>
          )}
          {success && !error && (
            <p role="status" className="text-sm font-semibold inline-flex items-center gap-1.5" style={{ color: "var(--bk-success)" }} data-testid="password-success">
              <CheckCircle size={16} weight="fill" /> {success}
            </p>
          )}
        </div>

        <div className="md:col-span-3 flex flex-wrap items-center gap-2">
          <button type="submit" disabled={!filled || saving} data-testid="password-save" className="bk-btn-primary inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold disabled:opacity-60">
            {saving ? <><CircleNotch size={16} className="animate-spin" /> Updating…</> : "Update password"}
          </button>
          <button type="button" onClick={cancel} disabled={saving || (!dirty && !error && !success)} data-testid="password-cancel" className="inline-flex items-center px-5 py-2.5 rounded-full text-sm font-semibold disabled:opacity-50" style={{ border: "1px solid var(--bk-border)", color: "var(--bk-text-2)", background: "#fff" }}>
            Cancel
          </button>
        </div>
      </form>
    </section>
  );
}
